import { z } from 'zod';
import type { Microsoft365Source } from './microsoft365-files.js';

/**
 * Documents in the API (Phase 12, 06 §13, ADR-0011; DOC-001…DOC-014). Part 3 of Microsoft 365 (DOC-017…): a
 * document may be a copy of a OneDrive, SharePoint or Outlook file (`source`), copied by the worker first (stage
 * `importing`).
 */
/** 'upload': attached on the home page, before its conversation exists; the uploader's only */
export type DocumentScope = 'conversation' | 'user' | 'workspace' | 'organization' | 'upload';
export type DocumentKindDto = 'pdf' | 'docx' | 'pptx' | 'xlsx' | 'csv' | 'text' | 'markdown' | 'image' | 'other';

export interface DocumentNoteDto {
  code: string;
  params?: Record<string, string | number>;
}

/**
 * Where a copied document came from (Belgeler's "Kaynak", the aside's box): names only, never a URL or a Graph id.
 * The client words the label ("SharePoint · Finans", "OneDrive", "Outlook eki · Can Aydın").
 */
export interface DocumentSourceDto {
  provider: Microsoft365Source;
  /** SharePoint: the site's name ("Finans") */
  siteName: string | null;
  /** Outlook: the mail's sender ("Can Aydın") */
  senderName: string | null;
  /** "SharePoint › Finans › Belgeler" */
  path: string | null;
  /** the copy's time and who made it ("Getiren"), by name */
  fetchedAt: string | null;
  fetchedBy: string | null;
  /** the source's last change as copied ("Kaynaktaki"); a viewer's own check may say more (DocumentSourceCheckDto) */
  sourceModifiedAt: string | null;
  sourceModifiedBy: string | null;
  /** the last copy that failed: a code (DOCUMENT_SOURCE_ERRORS) and when; the old copy stays */
  lastError: string | null;
  lastErrorAt: string | null;
}

export interface DocumentDto {
  id: string;
  scope: DocumentScope;
  name: string;
  mediaType: string;
  kind: DocumentKindDto;
  size: number;
  status: 'queued' | 'processing' | 'ready' | 'failed';
  /** importing: a Microsoft 365 copy being fetched (the first copy or "Yeniden al") */
  stage: 'importing' | 'extracting' | 'converting' | 'ocr' | 'indexing' | null;
  progress: { done: number; total: number } | null;
  errorCode: string | null;
  pages: number | null;
  sheets: number | null;
  tokens: number | null;
  chunks: number | null;
  ocr: { pages: number; quality: 'good' | 'fair' | 'low'; confidence: number } | null;
  /** said to the user before the first question (DOC-008, QW-BUG-023) */
  notes: DocumentNoteDto[];
  memoryAllowed: boolean;
  workspaceId: string | null;
  conversationId: string | null;
  /** an attachment's conversation, as its title (Belgeler: "… sohbetinde") */
  conversationTitle: string | null;
  /** the conversation upload it was made from (an attachment) */
  fileId: string | null;
  /** the caller may rename, share and delete it */
  canManage: boolean;
  /** what the preview shows: a PDF (also the rendition of Word and PowerPoint), sheets, text or a picture */
  preview: 'pdf' | 'sheets' | 'text' | 'image' | null;
  /** DOC-002: the whole text against the conversation model's window, and what one question roughly adds */
  estimate: { tokens: number; fitsContext: boolean; questionTokens: number } | null;
  createdAt: string;
  updatedAt: string;
  processedAt: string | null;
  /** part 3: a copy of a Microsoft 365 file; absent or null for an upload (older clients ignore it, D-202) */
  source?: DocumentSourceDto | null;
  /** part 3: "Yeniden al" is offered (the caller manages it, a OneDrive or SharePoint source, a ready copy; a failed one is tried again with "Yeniden dene") */
  canRefetch?: boolean;
}

export interface DocumentSheetDto {
  name: string;
  rows: [number, string[]][];
  columns: number;
  truncated: boolean;
}

export interface DocumentPartDto {
  kind: 'page' | 'sheet' | 'slide' | 'section';
  number: number;
  label: string | null;
  text: string;
  ocr: boolean;
}

export const documentScope = z.enum(['user', 'workspace', 'organization']);
export const documentUploadQuery = z.object({
  scope: documentScope.default('user'),
  workspaceId: z.uuid().optional(),
  conversationId: z.uuid().optional(),
  projectId: z.uuid().optional(),
});
/** Belgeler's "Kaynak: Tümü ▾": uploaded from the computer (no source) or copied from a Microsoft 365 source. */
export const documentSourceFilter = z.enum(['upload', 'onedrive', 'sharepoint', 'outlook']);
export const documentListQuery = z.object({
  scope: z.enum(['conversation', 'user', 'workspace', 'organization']).optional(),
  workspaceId: z.uuid().optional(),
  q: z.string().trim().max(200).optional(),
  /** part 3: upload = no source ("Bilgisayardan") */
  source: documentSourceFilter.optional(),
  before: z.iso.datetime().optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
});
export const documentPatchBody = z
  .object({
    name: z.string().trim().min(1).max(255).regex(/^[^\\/\0]+$/).optional(),
    memoryAllowed: z.boolean().optional(),
    /** keep an attachment in the library, or share a document with a workspace or the organization */
    scope: documentScope.optional(),
    workspaceId: z.uuid().optional(),
    /**
     * part 3: the warning's "Anladım" when a document with a Microsoft 365 source moves to a workspace or the
     * organization (everyone there sees it, also people who cannot see the file in Microsoft 365)
     */
    acknowledged: z.boolean().optional(),
  })
  .refine((v) => Object.entries(v).some(([key, x]) => key !== 'acknowledged' && x !== undefined), { message: 'nothing to update' });
export const documentLinkBody = z.object({ documentId: z.uuid() });
export const documentPartsQuery = z.object({
  from: z.coerce.number().int().min(1).optional(),
  to: z.coerce.number().int().min(1).optional(),
});
